export const PWA_UPDATE_EVENT = "classroom-pwa-update-ready";
export const PWA_STATUS_EVENT = "classroom-pwa-update-status";
export const PWA_CHECK_EVENT = "classroom-pwa-check-update";

export type PwaUpdateKind =
  | "idle"
  | "checking"
  | "current"
  | "downloading"
  | "ready"
  | "auth-required"
  | "offline"
  | "failed";
export type PwaUpdateState = { kind: PwaUpdateKind; availableBuild?: string; checkedAt?: number };
let updateState: PwaUpdateState = { kind: "idle" };
let checking: Promise<void> | undefined;
let workerUrl = "/sw.js";
const observed = new WeakSet<ServiceWorkerRegistration>();
const accessChannelName = "classroom-pwa-website-access";
let accessChannel: BroadcastChannel | undefined;

export function notifyPwaAccessRestored() {
  if (!("BroadcastChannel" in window)) return;
  try {
    const channel = new window.BroadcastChannel(accessChannelName);
    channel.postMessage("access-restored");
    channel.close();
  } catch {
    // Restricted browser contexts still recover on focus or the periodic check.
  }
}

export class PwaUpdateError extends Error {
  constructor(readonly code: "auth-required" | "offline" | "failed" | "changed") {
    super(code);
  }
}

export function getPwaUpdateState() {
  return updateState;
}

function publish(state: PwaUpdateState) {
  updateState = state;
  window.dispatchEvent(new CustomEvent(PWA_STATUS_EVENT));
  if (state.kind === "ready") window.dispatchEvent(new CustomEvent(PWA_UPDATE_EVENT));
}

async function websiteBuild() {
  if (navigator.onLine === false) throw new PwaUpdateError("offline");
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 10_000);
  try {
    const response = await fetch("/version.json", {
      cache: "no-store",
      credentials: "same-origin",
      redirect: "manual",
      signal: controller.signal,
    });
    // Access can return an opaque redirect rather than a readable login response.
    if (response.type === "opaqueredirect" || response.status === 401 || response.status === 403 || response.redirected)
      throw new PwaUpdateError("auth-required");
    if (!response.ok || !response.headers.get("Content-Type")?.includes("application/json"))
      throw new PwaUpdateError("failed");
    const value: unknown = await response.json();
    if (
      !value ||
      typeof value !== "object" ||
      !("buildId" in value) ||
      typeof value.buildId !== "string" ||
      !/^[a-zA-Z0-9_-]{1,80}$/.test(value.buildId)
    )
      throw new PwaUpdateError("failed");
    return value.buildId;
  } finally {
    window.clearTimeout(timeout);
  }
}

function workerBuild(worker: ServiceWorker) {
  return new Promise<string>((resolve, reject) => {
    const channel = new MessageChannel();
    const close = () => {
      window.clearTimeout(timeout);
      channel.port1.close();
    };
    const timeout = window.setTimeout(() => {
      close();
      reject(new PwaUpdateError("failed"));
    }, 2_000);
    channel.port1.onmessage = (event: MessageEvent<{ buildId?: unknown }>) => {
      close();
      if (typeof event.data?.buildId === "string") resolve(event.data.buildId);
      else reject(new PwaUpdateError("failed"));
    };
    try {
      worker.postMessage({ type: "GET_BUILD_ID" }, [channel.port2]);
    } catch {
      close();
      reject(new PwaUpdateError("failed"));
    }
  });
}

async function bounded<T>(task: Promise<T>, milliseconds: number) {
  let timer = 0;
  try {
    return await Promise.race([
      task,
      new Promise<never>((_resolve, reject) => {
        timer = window.setTimeout(() => reject(new PwaUpdateError("failed")), milliseconds);
      }),
    ]);
  } finally {
    window.clearTimeout(timer);
  }
}

function installed(worker: ServiceWorker) {
  return new Promise<void>((resolve, reject) => {
    const cleanup = () => {
      window.clearTimeout(timeout);
      worker.removeEventListener("statechange", changed);
    };
    const timeout = window.setTimeout(() => {
      cleanup();
      reject(new PwaUpdateError("failed"));
    }, 30_000);
    const changed = () => {
      if (!["installed", "activated", "redundant"].includes(worker.state)) return;
      cleanup();
      if (worker.state === "redundant") reject(new PwaUpdateError("failed"));
      else resolve();
    };
    worker.addEventListener("statechange", changed);
    changed();
  });
}

function observe(registration: ServiceWorkerRegistration) {
  if (observed.has(registration)) return;
  observed.add(registration);
  registration.addEventListener("updatefound", () => {
    const worker = registration.installing;
    worker?.addEventListener("statechange", () => {
      if (worker.state === "installed" && !checking) void checkForPwaUpdate({ background: true });
      if (worker.state === "redundant" && updateState.kind !== "ready") publish({ kind: "failed" });
    });
  });
}

export function checkForPwaUpdate(options: { background?: boolean } = {}): Promise<void> {
  if (checking) return checking;
  // Rechecking a downloaded build must not reopen a reminder the teacher collapsed.
  const pendingBuild = options.background && updateState.kind === "ready" ? updateState.availableBuild : undefined;
  checking = (async () => {
    if (!pendingBuild) publish({ kind: "checking" });
    try {
      const availableBuild = await websiteBuild();
      if (!("serviceWorker" in navigator)) throw new PwaUpdateError("failed");
      const registration = await bounded(
        navigator.serviceWorker.getRegistration("/").then((current) => {
          // A retained registration with no worker cannot be repaired by
          // update(); register again without removing any existing data.
          if (current && (current.active || current.waiting || current.installing)) return current;
          return navigator.serviceWorker.register(workerUrl, { scope: "/", updateViaCache: "none" });
        }),
        15_000,
      );
      observe(registration);
      if (availableBuild !== __APP_BUILD_ID__ && availableBuild !== pendingBuild)
        publish({ kind: "downloading", availableBuild });
      if (registration.installing) await installed(registration.installing);
      await bounded(registration.update(), 15_000);
      if (availableBuild === __APP_BUILD_ID__) {
        publish({ kind: "current", availableBuild, checkedAt: Date.now() });
        return;
      }
      if (registration.installing) await installed(registration.installing);
      // A restored page can still run the old program after its worker activated.
      // It needs the same save-confirmed reload, even with no waiting worker.
      const worker = registration.waiting || registration.active;
      if (!worker || (await workerBuild(worker)) !== availableBuild) throw new PwaUpdateError("failed");
      publish({ kind: "ready", availableBuild, checkedAt: Date.now() });
    } catch (error) {
      publish({
        kind:
          navigator.onLine === false
            ? "offline"
            : error instanceof PwaUpdateError && error.code === "auth-required"
              ? "auth-required"
              : "failed",
      });
    }
  })().finally(() => {
    checking = undefined;
  });
  return checking;
}

export async function applyPwaUpdate() {
  if (!("serviceWorker" in navigator)) throw new Error("此瀏覽器未能準備更新，請保存後關閉並重新開啟。");
  const registration = await navigator.serviceWorker.getRegistration("/");
  const worker = registration?.waiting || registration?.active;
  if (!worker) throw new Error("新版尚未下載完成，請保持連線並稍後再試。");

  // A newer deployment may arrive while the teacher is saving. Never activate
  // yesterday's waiting worker just because its download once succeeded.
  if ((await websiteBuild()) !== (await workerBuild(worker))) {
    void checkForPwaUpdate();
    throw new PwaUpdateError("changed");
  }

  if (worker.state === "activated") {
    const current = await navigator.serviceWorker.getRegistration("/");
    if (current?.active !== worker) throw new PwaUpdateError("changed");
    return;
  }

  await new Promise<void>((resolve, reject) => {
    const channel = new MessageChannel();
    let settled = false;
    let checking = false;
    const cleanup = () => {
      window.clearTimeout(timeout);
      window.clearInterval(poll);
      navigator.serviceWorker.removeEventListener("controllerchange", changed);
      worker.removeEventListener("statechange", changed);
      channel.port1.close();
    };
    const failed = (error: Error) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };
    const activated = async () => {
      if (settled || checking || worker.state !== "activated") return;
      checking = true;
      try {
        const current = await navigator.serviceWorker.getRegistration("/");
        // WebKit may activate the requested worker without delivering the
        // controllerchange event to the page. Verify that exact worker before
        // allowing a reload; the APPLY_UPDATE acknowledgement alone is too early.
        if (!settled && current?.active === worker) {
          settled = true;
          cleanup();
          resolve();
        }
      } catch {
        // A transient inspection failure leaves the current page intact.
      } finally {
        checking = false;
      }
    };
    const changed = () => {
      void activated();
    };
    const timeout = window.setTimeout(() => {
      failed(new Error("更新暫時未能完成。請保存後關閉此網站的所有分頁，再重新開啟。"));
    }, 15_000);
    const poll = window.setInterval(changed, 250);
    navigator.serviceWorker.addEventListener("controllerchange", changed);
    worker.addEventListener("statechange", changed);
    channel.port1.onmessage = (event: MessageEvent<{ applied: boolean }>) => {
      if (!event.data.applied) {
        failed(new Error("請先保存並關閉此網站的其他分頁或 PWA 視窗，再更新。"));
      } else changed();
    };
    try {
      worker.postMessage({ type: "APPLY_UPDATE" }, [channel.port2]);
      changed();
    } catch (error) {
      failed(error instanceof Error ? error : new Error("新版尚未下載完成，請保持連線並稍後再試。"));
    }
  });
}

export function registerPwa(url = "/sw.js") {
  if (!import.meta.env.PROD || !("serviceWorker" in navigator)) return;
  workerUrl = url;
  const check = () => {
    void checkForPwaUpdate({ background: true });
  };
  if ("BroadcastChannel" in window && !accessChannel) {
    try {
      accessChannel = new window.BroadcastChannel(accessChannelName);
      accessChannel.addEventListener("message", (event: MessageEvent<unknown>) => {
        // This is only a retry signal. The version request must still pass Access.
        if (event.data === "access-restored") check();
      });
    } catch {
      // Do not let an unavailable notification channel disable other retries.
    }
  }
  // Keep recovery listeners even when the initial request is blocked by Access.
  window.addEventListener("online", check);
  window.addEventListener("focus", check);
  window.addEventListener("pageshow", check);
  window.addEventListener("offline", () => publish({ kind: "offline" }));
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") check();
  });
  window.setInterval(() => {
    if (document.visibilityState === "visible") check();
  }, 60_000);
  if (document.readyState === "complete") check();
  else window.addEventListener("load", check, { once: true });
}
